import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
// setCustomNativeDragPreview ships as its own subpath entry point (not re-
// exported from /element/adapter in p-dnd 1.8.x).
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview"
import { preserveOffsetOnSource } from "@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source"
// auto-scroll 2.1.5 exports both entry points from `/element` — the window
// variant is new dependency USAGE, not a new dependency (anti-idea 6).
import {
  autoScrollForElements,
  autoScrollWindowForElements,
} from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element"
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import { Controller } from "@hotwired/stimulus"

import { appendSlot, directionOf, neighbourSlot } from "../../../lib/work/rank.js"
import { ITEM_TYPE, isWorkDragSource, resolveMove } from "../../../lib/work/routing.js"

// Work kit drag kernel (W1 Track A) — the shared multi-container engine every
// later work surface (board, backlog, grid, timeline, page tree) consumes
// read-only. Pattern source: ui--draggable-list (single-list monitor, item
// lifecycle wiring, edge paint grammar) — patterned, never forked or edited.
//
// ── The whole DOM API is events (anti-idea 2 — ZERO network, no URL value) ──
//
//   ui--work:drag-start  { item_id, container }         // lifecycle only
//   ui--work:drop        { container, from_container,   // the ONE intent event;
//                          moved_ids[], before_id,      // neighbour deltas only —
//                          after_id, gesture_token,     // never a full array, never
//                          source: "pointer"|"keyboard" // index math (anti-idea 3)
//                        }
//   ui--work:drag-cancel { item_id, container }         // Esc / teardown visibility
//
// Every drop carries a per-gesture `gesture_token`; consumers translate it
// verbatim into their surface events, so `revert(token)` is reachable from any
// surface without a kernel amendment (F4 Layer 0 seam). revert(token) restores
// the pre-gesture container order exactly — optimistic-drop consumers call it
// when their persistence fails. Reach it via Stimulus:
//   application.getControllerForElementAndIdentifier(el, "ui--work-drag").revert(token)
//
// ── Data-attribute contract (surfaces emit exactly these) ──────────────────
//
//   <div data-controller="ui--work-drag">
//     <ul data-ui--work-drag-target="container" data-container-id="col-todo"
//         data-axis="vertical"            (optional; "vertical"|"horizontal")
//         data-nest-kind="folder"         (optional → nest-highlight drop mode)
//         data-column-id data-lane-id …>  (any surface attrs are forwarded)
//       <li data-ui--work-drag-target="item" data-item-id="WORK-1"
//           data-nest-kind=…>             (optional handle/inner/preview targets)
//         <span data-ui--work-drag-target="handle">…</span>
//         <div data-ui--work-drag-target="inner">…</div>
//       </li>
//     </ul>
//     <template data-ui--work-drag-target="preview">…ghost markup…</template>
//   </div>
//
// Container AND item targets forward their ENTIRE data-attribute set into
// getInitialData (normalized: `data-column-id` → `column_id`), so surfaces
// carry column_id/lane_id/kind metadata through every event without kernel
// edits. Container ids are opaque strings — no board vocabulary here (the
// engines stay split).
//
// ── Coexistence (anti-idea 8) ───────────────────────────────────────────────
//
// ITEM_TYPE is distinct from draggable_list's and tag_input's; every
// canMonitor/canDrop/canScroll filters on it, so a draggable-list drag never
// trips this kernel and vice versa on a shared page.
//
// ── Accessibility ───────────────────────────────────────────────────────────
//
// Keyboard rank: Alt+ArrowUp/Down moves the focused item within its container
// and emits the SAME drop payload with source: "keyboard" (cross-container
// keyboard move is W2's roving concern). Announcements flow into an aria-live
// region, host-overridable via the `announcements` value — keys are fixed,
// values are free text with {token} interpolation (flow's F9 pattern).
//
// Custom drag preview: the `preview` template's markup renders into a DOM
// portal at the browser's native preview size, ghost offset preserved from the
// source grab point (preserveOffsetOnSource policy — ported as prose/policy
// from conek-rails draggable-card.tsx, never React code, R6).

// Live-region copy defaults (host-overridable via the `announcements` value;
// a missing key falls back to these, so partial overrides stay complete).
const DEFAULT_ANNOUNCEMENTS = {
  dragStarted: "Dragging {item_id}",
  moved: "Moved {item_id} in {container_id}",
  dropped: "Dropped {item_id} in {container_id}",
  cancelled: "Drag cancelled",
}

// Kernel event names — the "ui--work" family prefix is deliberate (the spec's
// whole Track A DOM API) and NOT Stimulus's default `${identifier}:action`
// shape, so events are dispatched as CustomEvents directly.
const DROP_EVENT = "ui--work:drop"
const DRAG_START_EVENT = "ui--work:drag-start"
const DRAG_CANCEL_EVENT = "ui--work:drag-cancel"

// Drop-affordance class grammar — identical to draggable_list's (anti-idea 8:
// pattern, never edit the source). Vertical edge lines; horizontal containers
// paint the left/right twins. Nest mode paints a wide highlight instead of an
// edge line (tree-style surfaces, W9).
const EDGE_CLASSES = ["border-t-2", "border-b-2", "border-l-2", "border-r-2", "border-primary"]
const NEST_CLASSES = ["bg-primary/10", "ring-2", "ring-inset", "ring-primary"]
const MUTED_CLASS = "opacity-40"

// Gesture-scoped snapshots kept for revert(token) — FIFO-capped so consumers
// that never revert cannot grow the map unbounded on a long-lived board.
const SNAPSHOT_CAP = 20

let tokenCounter = 0
let rootCounter = 0
function newGestureToken() {
  tokenCounter += 1
  if (globalThis.crypto?.randomUUID) {
    return `work-drag-${tokenCounter}-${globalThis.crypto.randomUUID()}`
  }
  return `work-drag-${tokenCounter}-${Math.random().toString(36).slice(2)}`
}

// Every data-attribute of `element`, normalized (`data-column-id` →
// `column_id`). This IS the surface pass-through rule: nothing is filtered or
// renamed beyond the prefix/dash normalization, so surface-supplied fields
// reach every event payload without kernel edits.
function forwardSurface(element) {
  const surface = {}
  for (const attr of element.attributes) {
    if (!attr.name.startsWith("data-")) continue
    surface[attr.name.slice(5).replace(/-/g, "_")] = attr.value
  }
  return surface
}

const CONTAINER_SELECTOR = "[data-ui--work-drag-target='container']"
const ITEM_SELECTOR = "[data-ui--work-drag-target='item']"

export default class extends Controller {
  static targets = ["container", "item", "handle", "preview", "announcer"]

  static values = {
    // Host-overridable live-region copy. Keys are fixed (see
    // DEFAULT_ANNOUNCEMENTS), values are free text with {token} interpolation.
    announcements: { type: Object, default: {} },
  }

  connect() {
    // `itemTargetConnected` can fire before `connect()`, so init lazily there
    // too. The flag keeps Turbo cache-restore reconnects from double-register
    // -ing p-dnd attachments (D1 caveat: idempotent connect, full teardown).
    if (this._connected) return
    this._connected = true

    // p-dnd's monitor/drop-target registries are GLOBAL: with two kernel
    // roots on one page (board + backlog, W2), every root otherwise accepts
    // every work drag. The root id scopes each root's monitor, drop targets
    // and auto-scroll to its own sources — a foreign-root source is not a
    // drop intent anywhere outside its own root.
    rootCounter += 1
    this._rootId = `ui--work-drag-root-${rootCounter}`

    this._cleanups = [
      // ONE monitor routes all containers — reorder and cross-container move
      // resolve through the same path (dossier F1). canMonitor receives the
      // {initial, source} feedback object — destructure before the filter.
      monitorForElements({
        canMonitor: ({ source }) => this._isOwnSource(source),
        onDrop: ({ source, location }) => this._onMonitorDrop(source, location),
      }),
      // Window auto-scroll is new dependency USAGE, not a new dependency
      // (anti-idea 6) — the auto-scroll package ships both entry points.
      autoScrollWindowForElements(),
    ]

    this._itemCleanups = new Map()
    this._containerCleanups = new Map()
    this._snapshots = new Map()
    this._gesture = null
    this._escBound = false
    this._ensureAnnouncer()
  }

  disconnect() {
    if (!this._connected) return
    this._connected = false

    // Mid-gesture teardown is a clean no-op: styling cleared, Esc unbound,
    // gesture dropped. p-dnd attachments die with their cleanups, so a
    // reconnect after Turbo restore never double-registers.
    this._clearGestureStyles()
    this._unbindEsc()
    this._gesture = null

    this._cleanups.forEach((fn) => fn())
    this._cleanups = []
    this._itemCleanups.forEach((fn) => fn())
    this._itemCleanups.clear()
    this._containerCleanups.forEach((fn) => fn())
    this._containerCleanups.clear()
    this._snapshots.clear()
    this._removeAnnouncer()
  }

  // ── Target lifecycle ──────────────────────────────────────────────────────
  // Per-target wiring mirrors draggable_list's itemTargetConnected discipline:
  // runtime-added rows/containers become draggable without re-initialising.

  containerTargetConnected(container) {
    this._containerCleanups ||= new Map()
    if (this._containerCleanups.has(container)) return

    const cleanupDropTarget = dropTargetForElements({
      element: container,
      getIsSticky: () => true,
      canDrop: ({ source }) => this._isOwnSource(source),
      getData: ({ element }) => this._containerData(container),
      onDragEnter: ({ self }) => this._onContainerOver(container, self),
      onDrag: ({ self }) => this._onContainerOver(container, self),
      onDragLeave: () => this._clearNest(container),
      onDrop: () => this._clearNest(container),
    })

    const cleanupAutoScroll = autoScrollForElements({
      element: container,
      canScroll: ({ source }) => this._isOwnSource(source),
      getConfiguration: () => ({ maxScrollSpeed: "fast" }),
    })

    this._containerCleanups.set(container, () => {
      cleanupDropTarget()
      cleanupAutoScroll()
    })
  }

  containerTargetDisconnected(container) {
    const cleanup = this._containerCleanups?.get(container)
    if (cleanup) {
      cleanup()
      this._containerCleanups.delete(container)
    }
  }

  itemTargetConnected(item) {
    this._itemCleanups ||= new Map()
    if (this._itemCleanups.has(item)) return

    const handle = item.querySelector("[data-ui--work-drag-target='handle']")
    const axis = this._axisOf(item)

    // Keyboard rank is the guaranteed cross-input path (P3): every item is
    // focusable unless the surface says otherwise.
    if (!item.hasAttribute("tabindex")) item.setAttribute("tabindex", "0")

    const cleanupDraggable = draggable({
      element: item,
      dragHandle: handle || item,
      getInitialData: () => this._itemData(item),
      onGenerateDragPreview: ({ source, nativeSetDragImage, location }) =>
        this._generateDragPreview({ source, nativeSetDragImage, location }),
      onDragStart: () => this._onGestureStart(item),
      onDrop: () => this._unmute(item),
    })

    const cleanupDropTarget = dropTargetForElements({
      element: item,
      getIsSticky: () => true,
      canDrop: ({ source }) => this._isOwnSource(source),
      getData: ({ element, input }) =>
        attachClosestEdge(this._itemData(item), {
          element,
          input,
          allowedEdges: axis === "horizontal" ? ["left", "right"] : ["top", "bottom"],
        }),
      onDragEnter: ({ source, self }) => {
        if (source.element === item) return
        this._onItemOver(item, self)
      },
      onDrag: ({ source, self }) => {
        if (source.element === item) return
        this._onItemOver(item, self)
      },
      onDragLeave: () => this._clearAffordances(item),
      onDrop: () => this._clearAffordances(item),
    })

    const onKeydown = (event) => this._onItemKeydown(event, item)
    item.addEventListener("keydown", onKeydown)

    this._itemCleanups.set(item, () => {
      cleanupDraggable()
      cleanupDropTarget()
      item.removeEventListener("keydown", onKeydown)
    })
  }

  itemTargetDisconnected(item) {
    const cleanup = this._itemCleanups?.get(item)
    if (cleanup) {
      cleanup()
      this._itemCleanups.delete(item)
    }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  // Restore the pre-gesture container order exactly (F4 Layer 0 seam). The
  // token is the `gesture_token` carried by every ui--work:drop detail —
  // consumers pass it back verbatim. One-shot: the snapshot is consumed on a
  // successful restore. Returns whether the token was known.
  revert(token) {
    const snapshot = token && this._snapshots?.get(token)
    if (!snapshot) return false

    snapshot.forEach(({ container, items }) => {
      items.forEach((item) => container.appendChild(item))
    })
    this._snapshots.delete(token)
    return true
  }

  // ── Gesture lifecycle ─────────────────────────────────────────────────────

  _onGestureStart(item) {
    const container = this._containerOf(item)
    const token = newGestureToken()
    const snapshot = this._captureSnapshot()

    this._snapshots.set(token, snapshot)
    this._evictOldSnapshots()
    this._gesture = { token, item }

    // Drag-source muting follows the draggable_list grammar (:68) — on the
    // inner wrapper, `opacity-40` for the gesture's duration.
    this._inner(item).classList.add(MUTED_CLASS)
    this._bindEsc()

    this._emit(DRAG_START_EVENT, {
      item_id: item.dataset.itemId,
      container: container?.dataset.containerId ?? null,
    })
    this._announce("dragStarted", {
      item_id: item.dataset.itemId,
      container_id: container?.dataset.containerId ?? null,
    })
  }

  _onMonitorDrop(source, location) {
    // A cancelled gesture (Esc, dragend outside every target, teardown) lands
    // here with an empty drop-target list — never a drop intent.
    if (!this._gesture) return

    const descriptor = resolveMove({ source, target: location.current.dropTargets[0] })
    if (!descriptor) {
      this._endGesture()
      return
    }

    const containerEl = this._containerById(descriptor.container_id)
    const order = containerEl ? this._itemIds(containerEl) : []
    const movedIds = [descriptor.item_id]
    const axis = containerEl?.dataset.axis === "horizontal" ? "horizontal" : "vertical"
    const direction = containerEl ? directionOf(containerEl) : "ltr"

    const slot =
      descriptor.kind === "item"
        ? neighbourSlot({
            order,
            movedIds,
            targetId: descriptor.target_id,
            edge: descriptor.edge,
            direction,
            axis,
          })
        : appendSlot({ order, movedIds })

    if (!slot) {
      this._endGesture()
      return
    }

    if (containerEl) this._applySlot(containerEl, movedIds, slot)
    this._clearGestureStyles()
    this._unbindEsc()

    const detail = {
      container: descriptor.container_id,
      from_container: descriptor.from_container,
      moved_ids: movedIds,
      before_id: slot.before_id,
      after_id: slot.after_id,
      gesture_token: this._gesture.token,
      source: "pointer",
    }
    this._gesture = null

    this._emit(DROP_EVENT, detail)
    this._announce("dropped", {
      item_id: detail.moved_ids[detail.moved_ids.length - 1],
      container_id: detail.container,
    })
  }

  // A gesture that resolved to NO drop intent (drop outside every target,
  // self-drop, foreign type): styling cleared, Esc unbound, snapshot kept
  // for revert(), and the cancel IS published — browsers that suppress
  // keydown during the native drag loop (so _handleEsc never fires) still
  // produce an observable abort this way. (A throw in here would poison
  // p-dnd's lifecycle — it runs inside the drop dispatch, before cleanup.)
  _endGesture() {
    if (this._gesture) {
      const item = this._gesture.item
      this._emit(DRAG_CANCEL_EVENT, {
        item_id: item?.dataset.itemId ?? null,
        container: this._containerOf(item)?.dataset.containerId ?? null,
      })
      this._announce("cancelled", {})
    }
    this._clearGestureStyles()
    this._unbindEsc()
    this._gesture = null
  }

  // Esc mid-gesture: no ui--work:drop, styling cleared, order untouched, the
  // cancel event makes the abort visible (monitor-level flag-guarded listener
  // per the flow gesture discipline).
  _handleEsc(event) {
    if (event.key !== "Escape" || !this._gesture) return

    const item = this._gesture.item
    this._clearGestureStyles()
    this._unbindEsc()
    this._gesture = null

    this._emit(DRAG_CANCEL_EVENT, {
      item_id: item?.dataset.itemId ?? null,
      container: this._containerOf(item)?.dataset.containerId ?? null,
    })
    this._announce("cancelled", {})
  }

  _onItemKeydown(event, item) {
    if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return

    const container = this._containerOf(item)
    if (!container) return
    // Hidden items (a surface's filtered-out cards) are not rank candidates:
    // the gesture never displaces a card relative to invisible neighbours.
    const items = this._itemElements(container).filter((el) => !el.hasAttribute("hidden"))
    const index = items.indexOf(item)
    if (index === -1) return

    const neighbour = event.key === "ArrowUp" ? items[index - 1] : items[index + 1]
    if (!neighbour) return // already at the boundary — a no-op, not a move

    event.preventDefault()
    event.stopPropagation()

    const axis = container.dataset.axis === "horizontal" ? "horizontal" : "vertical"
    const direction = directionOf(container)
    const movedIds = [item.dataset.itemId]
    const token = newGestureToken()
    const snapshot = this._captureSnapshot()
    this._snapshots.set(token, snapshot)
    this._evictOldSnapshots()

    const slot = neighbourSlot({
      order: items.map((el) => el.dataset.itemId),
      movedIds,
      targetId: neighbour.dataset.itemId,
      edge: event.key === "ArrowUp" ? "top" : "bottom",
      direction,
      axis,
    })
    if (!slot) return

    this._applySlot(container, movedIds, slot)

    this._emit(DROP_EVENT, {
      container: container.dataset.containerId ?? null,
      from_container: container.dataset.containerId ?? null,
      moved_ids: movedIds,
      before_id: slot.before_id,
      after_id: slot.after_id,
      gesture_token: token,
      source: "keyboard",
    })
    this._announce("moved", {
      item_id: item.dataset.itemId,
      container_id: container.dataset.containerId ?? null,
    })
  }

  // ── Drop affordances ──────────────────────────────────────────────────────

  _onItemOver(item, self) {
    // Nest-mode targets (surface-supplied data-nest-kind, forwarded by the
    // pass-through rule) paint the wide nest highlight INSTEAD of an edge line
    // — a tree node is a folder drop, not an edge insert.
    if (self.data?.surface?.nest_kind) {
      this._clearEdge(item)
      this._paintNest(item)
      return
    }
    this._clearNest(item)
    this._paintEdge(item, extractClosestEdge(self.data))
  }

  _onContainerOver(container, self) {
    if (!self.data?.surface?.nest_kind) return
    this._paintNest(container)
  }

  // The draggable_list edge grammar (:168-184), with the horizontal twins for
  // axis: "horizontal" containers. Painted on the item's inner wrapper.
  _paintEdge(item, edge) {
    if (!edge) return
    const inner = this._inner(item)
    this._clearEdge(item)
    const horizontal = edge === "left" || edge === "right"
    inner.classList.add(
      horizontal ? (edge === "left" ? "border-l-2" : "border-r-2") : edge === "top" ? "border-t-2" : "border-b-2",
      "border-primary",
    )
  }

  _clearEdge(item) {
    this._inner(item).classList.remove(...EDGE_CLASSES)
  }

  _paintNest(element) {
    this._inner(element).classList.add(...NEST_CLASSES)
  }

  _clearNest(element) {
    this._inner(element).classList.remove(...NEST_CLASSES)
  }

  _clearAffordances(item) {
    this._clearEdge(item)
    this._clearNest(item)
  }

  _clearGestureStyles() {
    this.itemTargets.forEach((item) => {
      this._inner(item).classList.remove(MUTED_CLASS)
      this._clearAffordances(item)
    })
    this.containerTargets.forEach((container) => this._clearNest(container))
  }

  _unmute(item) {
    this._inner(item).classList.remove(MUTED_CLASS)
  }

  // ── DOM application (optimistic, revert(token)-backed) ────────────────────

  // Move the moved items into `slot` inside `container`. The slot is BETWEEN
  // before_id (the new predecessor) and after_id (the new successor): insert
  // after before_id, or before after_id when there is no predecessor, or
  // append when the container reads empty. movedIds keep their internal
  // order; cross-container moves re-parent through the same code — moved
  // elements are looked up across the WHOLE kernel scope (they are not in
  // the target container yet), while the anchor resolves against the target
  // container's live order.
  _applySlot(container, movedIds, slot) {
    const items = this._itemElements(container)
    const movedEls = movedIds
      .map((id) => this.itemTargets.find((el) => el.dataset.itemId === id))
      .filter(Boolean)

    if (slot.before_id) {
      // After the new predecessor (chains, keeping movedIds internal order).
      const anchor = items.find((el) => el.dataset.itemId === slot.before_id)
      if (anchor) {
        let cursor = anchor
        movedEls.forEach((el) => {
          cursor.after(el)
          cursor = el
        })
        return
      }
    }
    if (slot.after_id) {
      // No predecessor: directly before the new successor.
      const anchor = items.find((el) => el.dataset.itemId === slot.after_id)
      if (anchor) {
        movedEls.forEach((el) => anchor.before(el))
        return
      }
    }
    // Empty container (or both neighbours unresolvable): append at the end.
    movedEls.forEach((el) => container.appendChild(el))
  }

  _captureSnapshot() {
    return this.containerTargets.map((container) => ({
      container,
      items: this._itemElements(container),
    }))
  }

  _evictOldSnapshots() {
    while (this._snapshots.size > SNAPSHOT_CAP) {
      this._snapshots.delete(this._snapshots.keys().next().value)
    }
  }

  // ── Custom native drag preview ────────────────────────────────────────────

  // Render the `preview` template's markup into the portal the browser
  // photographs as the drag ghost. Offset policy (ported as prose from
  // conek-rails draggable-card.tsx's preserveOffsetOnSource): keep the ghost
  // anchored where the user grabbed the source, clamped to the preview size,
  // so the pointer never lands outside the ghost. p-dnd owns the portal's
  // lifecycle — it is removed from the DOM on the next drag lifecycle event,
  // so the ghost never survives dragend or teardown.
  _generateDragPreview({ source, nativeSetDragImage, location }) {
    const template = this.previewTargets[0]
    if (!template) return

    setCustomNativeDragPreview({
      nativeSetDragImage,
      getOffset: preserveOffsetOnSource({ element: source.element, input: location.current.input }),
      render: ({ container }) => {
        const ghost = template.content.firstElementChild?.cloneNode(true)
        if (!ghost) return undefined
        container.appendChild(ghost)
        return () => ghost.remove()
      },
    })
  }

  // ── Esc wiring (flag-guarded listener set) ────────────────────────────────

  _bindEsc() {
    if (this._escBound) return
    this._escBound = true
    this._escHandler = (event) => this._handleEsc(event)
    document.addEventListener("keydown", this._escHandler)
  }

  _unbindEsc() {
    if (!this._escBound) return
    this._escBound = false
    document.removeEventListener("keydown", this._escHandler)
    this._escHandler = null
  }

  // ── Live region ───────────────────────────────────────────────────────────

  _ensureAnnouncer() {
    if (this.hasAnnouncerTarget) return
    const el = document.createElement("div")
    el.setAttribute("data-ui--work-drag-target", "announcer")
    el.setAttribute("aria-live", "polite")
    el.setAttribute("aria-atomic", "true")
    el.className = "sr-only"
    this.element.appendChild(el)
    this._createdAnnouncer = el
  }

  _removeAnnouncer() {
    this._createdAnnouncer?.remove()
    this._createdAnnouncer = null
  }

  _announce(key, tokens) {
    const el = this.hasAnnouncerTarget ? this.announcerTarget : this._createdAnnouncer
    if (!el) return
    const messages = { ...DEFAULT_ANNOUNCEMENTS, ...(this.announcementsValue || {}) }
    el.textContent = String(messages[key] ?? "").replace(/\{(\w+)\}/g, (match, name) =>
      tokens[name] ?? match,
    )
  }

  // ── Plumbing ──────────────────────────────────────────────────────────────

  // Events carry the spec's "ui--work:*" names — NOT Stimulus's default
  // `${identifier}:action` shape — so they are dispatched as CustomEvents.
  _emit(name, detail) {
    this.element.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, cancelable: true }))
  }

  _itemData(item) {
    return {
      type: ITEM_TYPE,
      root_id: this._rootId,
      item_id: item.dataset.itemId ?? null,
      container_id: this._containerOf(item)?.dataset.containerId ?? null,
      surface: forwardSurface(item),
    }
  }

  _containerData(container) {
    return {
      type: ITEM_TYPE,
      root_id: this._rootId,
      container_id: container.dataset.containerId ?? null,
      surface: forwardSurface(container),
    }
  }

  // The root-scoped counterpart of isWorkDragSource: the source must carry
  // the work ITEM_TYPE AND this root's id (cross-root scoping, claudep
  // review: two roots on one page must not publish each other's drops).
  _isOwnSource(source) {
    return isWorkDragSource(source) && source.data.root_id === this._rootId
  }

  _containerOf(item) {
    return item.closest(CONTAINER_SELECTOR)
  }

  _axisOf(item) {
    return this._containerOf(item)?.dataset.axis === "horizontal" ? "horizontal" : "vertical"
  }

  _containerById(id) {
    return this.containerTargets.find((container) => (container.dataset.containerId ?? null) === id)
  }

  _itemElements(container) {
    return [...container.querySelectorAll(ITEM_SELECTOR)]
  }

  _itemIds(container) {
    return this._itemElements(container).map((el) => el.dataset.itemId)
  }

  // The item's inner wrapper carries dragging/drop-edge styling — falls back
  // to the item itself for consumers with no inner element (draggable_list
  // :160-166 grammar).
  _inner(item) {
    return item.querySelector("[data-ui--work-drag-target='inner']") || item
  }
}
